import { useEffect, useRef, useState } from 'react'

import { useToast } from '@/components/ui/toastContext'
import type { ApplicationStatus } from '@/features/recruiter/api/applicants.api'
import { HiredDocumentUpload } from '@/features/recruiter/components/HiredDocumentUpload'
import { StatusChangeModal } from '@/features/recruiter/components/StatusChangeModal'
import { useChangeApplicationStatus } from '@/features/recruiter/hooks/useApplicants'
import { getErrorMessage } from '@/utils/errors'

// Mirrors JobApplicationStatus::canRecruiterTransitionTo() in
// backend/app/Enums/JobApplicationStatus.php — Withdrawn is student-only.
const TRANSITIONS: Record<ApplicationStatus, ApplicationStatus[]> = {
  applied:      ['under_review', 'shortlisted', 'hired', 'rejected'],
  under_review: ['shortlisted', 'hired', 'rejected'],
  shortlisted:  ['hired', 'rejected'],
  hired:        [],
  rejected:     [],
  withdrawn:    [],
}

const LABELS: Record<ApplicationStatus, string> = {
  applied:      'Applied',
  under_review: 'Under Review',
  shortlisted:  'Shortlist',
  hired:        'Mark as Hired',
  rejected:     'Reject',
  withdrawn:    'Withdrawn',
}

const ITEM_ICON: Partial<Record<ApplicationStatus, string>> = {
  hired:        'ti-trophy text-success',
  rejected:     'ti-circle-x text-danger',
  under_review: 'ti-eye text-info',
  shortlisted:  'ti-star text-warning',
}

const MENU_WIDTH = 170

const ITEM_CLASS:Partial<Record<ApplicationStatus, string>> = {
  hired:    'fw-semibold text-success',
  rejected: 'text-danger',
}

export function ApplicantStatusActions({
  jobId,
  applicationId,
  status,
  showDocuments = false,
}: {
  jobId: number
  applicationId: number
  status: ApplicationStatus
  showDocuments?: boolean
}) {
  const changeStatus = useChangeApplicationStatus(jobId)
  const targets = TRANSITIONS[status]
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const [menuPos, setMenuPos] = useState<{ top?: number; bottom?: number; left: number } | null>(null)

  // Menu is position:fixed, so coordinates are viewport-relative — no
  // scroll offsets. Right-aligned to the button (last table column) and
  // flipped above it when there isn't room below.
  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      const menuHeight = targets.length * 36 + 16
      const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8))
      const fitsBelow = rect.bottom + 4 + menuHeight <= window.innerHeight
      setMenuPos(
        fitsBelow
          ? { top: rect.bottom + 4, left }
          : { bottom: window.innerHeight - rect.top + 4, left },
      )
    }
    setOpen((v) => !v)
  }

  // A fixed menu doesn't follow its button, so close it on scroll/resize.
  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  const toast = useToast()
  const [modalTarget, setModalTarget] = useState<'rejected' | 'hired' | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  const handleSelect = (target: ApplicationStatus) => {
    setOpen(false)
    if (target === 'rejected' || target === 'hired') {
      setModalTarget(target)
      setModalOpen(true)
      return
    }
    if (!confirm(`${LABELS[target]} this applicant?`)) return
    changeStatus.mutate({ applicationId, status: target })
  }

  const confirmModal = (payload: { notes?: string; offerLetter?: File }) => {
    if (!modalTarget) return
    changeStatus.mutate(
      { applicationId, status: modalTarget, ...payload },
      {
        onSuccess: () => {
          toast.success(modalTarget === 'hired' ? 'Candidate marked as hired.' : 'Candidate rejected.')
          setModalOpen(false)
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      },
    )
  }

  return (
    <div>
      {targets.length > 0 ? (
        <>
          <button
            ref={btnRef}
            type="button"
            className="btn btn-light btn-sm fs-13 d-inline-flex align-items-center gap-1"
            onClick={handleToggle}
            disabled={changeStatus.isPending}
          >
            {changeStatus.isPending ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <i className="ti ti-settings" />
            )}
            Actions
            <i className="ti ti-chevron-down fs-12" />
          </button>

          {open && (
            <>
              {/* full-page backdrop closes the menu on outside click */}
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 1040 }}
                onClick={() => setOpen(false)}
              />
              {/* fixed-position menu escapes table overflow:hidden */}
              <ul
                className="dropdown-menu show shadow-sm"
                style={{
                  position: 'fixed',
                  top:      menuPos?.top,
                  bottom:   menuPos?.bottom,
                  left:     menuPos?.left ?? 0,
                  zIndex:   1041,
                  minWidth: MENU_WIDTH,
                }}
              >
                {targets.map((target) => (
                  <li key={target}>
                    <button
                      type="button"
                      className={`dropdown-item fs-13 ${ITEM_CLASS[target] ?? ''}`}
                      onClick={() => handleSelect(target)}
                    >
                      {ITEM_ICON[target] && (
                        <i className={`ti ${ITEM_ICON[target]} me-2`} />
                      )}
                      {LABELS[target]}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      ) : (
        status === 'hired' ? (
          <span className="badge bg-success-transparent fs-12">
            <i className="ti ti-trophy me-1" />Hired
          </span>
        ) : status === 'rejected' ? (
          <span className="badge bg-danger-transparent fs-12">
            <i className="ti ti-circle-x me-1" />Rejected
          </span>
        ) : status === 'withdrawn' ? (
          <span className="badge bg-secondary-transparent fs-12">Withdrawn</span>
        ) : null
      )}

      {showDocuments && status === 'hired' && (
        <HiredDocumentUpload applicationId={applicationId} />
      )}

      {modalTarget !== null && (
        <StatusChangeModal
          show={modalOpen}
          target={modalTarget}
          loading={changeStatus.isPending}
          onClose={() => setModalOpen(false)}
          onConfirm={confirmModal}
        />
      )}
    </div>
  )
}
